// Copyright (C) 2012-present, The Authors. This program is free software: you can redistribute it and/or  modify it under the terms of the GNU Affero General Public License, version 3, as published by the Free Software Foundation. This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU Affero General Public License for more details. You should have received a copy of the GNU Affero General Public License along with this program.  If not, see <http://www.gnu.org/licenses/>.

import PropTypes from 'prop-types'
import { useState, useEffect, useCallback } from 'react'
import { Box, Heading, Text, Button, Input, Grid, Flex } from 'theme-ui'

import strings from '../../strings/strings'

const FIELDS = [
  { key: 'utm_source', labelKey: 'utm_source_label', placeholder: 'facebook, telegram, email' },
  { key: 'utm_medium', labelKey: 'utm_medium_label', placeholder: 'social, cpc, newsletter' },
  { key: 'utm_campaign', labelKey: 'utm_campaign_label', placeholder: 'launch2026' },
  { key: 'utm_term', labelKey: 'utm_term_label', placeholder: '' },
  { key: 'utm_content', labelKey: 'utm_content_label', placeholder: '' }
]

const EMPTY = { utm_source: '', utm_medium: '', utm_campaign: '', utm_term: '', utm_content: '' }

const storageKey = (cid) => `polis_utm_links_${cid}`

function buildLink(baseUrl, params) {
  const qs = FIELDS.filter(({ key }) => params[key] && params[key].trim())
    .map(({ key }) => `${key}=${encodeURIComponent(params[key].trim())}`)
    .join('&')
  if (!qs) return baseUrl
  return baseUrl + (baseUrl.includes('?') ? '&' : '?') + qs
}

function summarize(params) {
  return FIELDS.filter(({ key }) => params[key] && params[key].trim())
    .map(({ key }) => params[key].trim())
    .join(' · ')
}

const UtmLinkBuilder = ({ conversationId, baseUrl }) => {
  const [form, setForm] = useState(EMPTY)
  const [saved, setSaved] = useState([])
  const [copiedKey, setCopiedKey] = useState(null)

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey(conversationId))
      if (raw) setSaved(JSON.parse(raw))
    } catch {
      // storage unavailable — non-fatal
    }
  }, [conversationId])

  const persist = useCallback(
    (list) => {
      setSaved(list)
      try {
        localStorage.setItem(storageKey(conversationId), JSON.stringify(list))
      } catch {
        // non-fatal
      }
    },
    [conversationId]
  )

  const link = buildLink(baseUrl, form)
  const hasSource = !!(form.utm_source && form.utm_source.trim())

  const copy = (text, key) => {
    const done = () => {
      setCopiedKey(key)
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500)
    }
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done, () => {})
      }
    } catch {
      // clipboard unavailable — non-fatal
    }
  }

  const save = () => {
    if (!hasSource) return
    const entry = { id: Date.now(), params: { ...form }, link }
    persist([entry, ...saved])
  }

  const remove = (id) => persist(saved.filter((e) => e.id !== id))
  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  return (
    <Box sx={{ mb: [5], maxWidth: ['100%', '100%', '45em'] }}>
      <Heading as="h6" sx={{ fontSize: [1, null, 2], lineHeight: 'body', my: [3, null, 4] }}>
        {strings('utm_builder_heading')}
      </Heading>
      <Text sx={{ display: 'block', mb: [3], color: 'textSecondary', fontSize: 1 }}>
        {strings('utm_builder_hint')}
      </Text>

      <Grid gap={2} columns={[1, 2]} sx={{ mb: [3] }}>
        {FIELDS.map(({ key, labelKey, placeholder }) => (
          <Box key={key}>
            <Text sx={{ display: 'block', mb: 1, fontSize: 1 }}>{strings(labelKey)}</Text>
            <Input
              data-testid={`utm-${key}`}
              value={form[key]}
              placeholder={placeholder}
              onChange={setField(key)}
              sx={{ fontSize: 1 }}
            />
          </Box>
        ))}
      </Grid>

      <Text sx={{ display: 'block', mb: 1, fontSize: 1 }}>{strings('utm_generated_link')}</Text>
      <Flex sx={{ alignItems: 'stretch', gap: 2, mb: [2], flexWrap: 'wrap' }}>
        <Box
          sx={{
            flex: '1 1 20em',
            p: 2,
            bg: 'muted',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: 1,
            wordBreak: 'break-all'
          }}>
          {link}
        </Box>
        <Button
          onClick={() => copy(link, 'preview')}
          sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
          {copiedKey === 'preview' ? strings('utm_copied') : strings('utm_copy')}
        </Button>
        <Button
          variant="secondary"
          disabled={!hasSource}
          onClick={save}
          sx={{ cursor: hasSource ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}>
          {strings('utm_save')}
        </Button>
      </Flex>

      <Heading as="h6" sx={{ fontSize: [1, null, 2], lineHeight: 'body', mt: [4], mb: [2] }}>
        {strings('utm_saved_heading')}
      </Heading>
      {saved.length === 0 ? (
        <Text sx={{ color: 'textSecondary', fontSize: 1 }}>{strings('utm_empty')}</Text>
      ) : (
        <Box>
          {saved.map((entry) => (
            <Flex
              key={entry.id}
              sx={{
                alignItems: 'center',
                gap: 2,
                py: 2,
                borderBottom: '1px solid',
                borderColor: 'muted',
                flexWrap: 'wrap'
              }}>
              <Box sx={{ flex: '1 1 20em', minWidth: 0 }}>
                <Text sx={{ fontWeight: 'bold', fontSize: 1 }}>{summarize(entry.params)}</Text>
                <Text
                  sx={{
                    display: 'block',
                    fontFamily: 'monospace',
                    fontSize: 0,
                    color: 'textSecondary',
                    wordBreak: 'break-all'
                  }}>
                  {entry.link}
                </Text>
              </Box>
              <Button
                onClick={() => copy(entry.link, entry.id)}
                sx={{ cursor: 'pointer', fontSize: 1, px: 2, py: 1 }}>
                {copiedKey === entry.id ? strings('utm_copied') : strings('utm_copy')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => remove(entry.id)}
                sx={{ cursor: 'pointer', fontSize: 1, px: 2, py: 1 }}>
                {strings('utm_remove')}
              </Button>
            </Flex>
          ))}
        </Box>
      )}
    </Box>
  )
}

UtmLinkBuilder.propTypes = {
  conversationId: PropTypes.string.isRequired,
  baseUrl: PropTypes.string.isRequired
}

export default UtmLinkBuilder
